<template>
    <div data-testid="experience-skills-suggestions" class="experience-skills-suggestions">
        <Transition name="fade" mode="out-in">
            <template v-if="loading">
                <VJoyBlockSkeleton height="32px" width="100%" />
            </template>
            <div v-else-if="suggestedSkillsFiltered.length > 0" class="experience-skills-suggestions__tags">
                {{ $t('profile.section.experience.skills.suggestions') }}
                <VJoyTag
                    v-for="skill in suggestedSkillsShown"
                    :key="skill.id"
                    class="experience-skills-suggestions__item"
                    variant="secondary"
                    :disabled="disabled"
                    @click="addSuggestion(skill)"
                >
                    {{ skill.label }}
                    <VJoyIcon name="add" />
                </VJoyTag>
            </div>
        </Transition>
    </div>
</template>
<script setup lang="ts">
    import {computed, watch} from 'vue';
    import {storeToRefs, useDebounce} from '#imports';
    import {VJoyBlockSkeleton, VJoyIcon, VJoyTag} from '@maltjoy/core-vue';
    import type {Skill} from '@api';
    import {useSkillsSuggestionsStore} from '../../store/skillsSuggestionsStore';

    const {
        skills = [],
        description,
        disabled = false,
    } = defineProps<{
        skills: Skill[];
        description?: string;
        disabled?: boolean;
    }>();

    const skillsSuggestionsStore = useSkillsSuggestionsStore();
    skillsSuggestionsStore.resetSuggestedSkills();
    const {suggestedSkills, loading} = storeToRefs(skillsSuggestionsStore);
    const skillIds = computed(() => skills.map((skill) => skill.id));
    const suggestedSkillsFiltered = computed(() => suggestedSkills.value.filter((skill) => !skillIds.value.includes(skill.id)));
    const NUMBER_OF_SKILLS_SHOWN = 5;
    const suggestedSkillsShown = computed(() => suggestedSkillsFiltered.value.slice(0, NUMBER_OF_SKILLS_SHOWN));

    const {debounce} = useDebounce();
    watch(
        () => description,
        () => {
            debounce(suggestSkills, 600);
        },
        {immediate: true},
    );

    async function suggestSkills() {
        if (!description) {
            return;
        }
        await skillsSuggestionsStore.suggestSkills(description);
    }

    const emit = defineEmits<{
        skillSelected: [skill: Skill];
    }>();

    function addSuggestion(suggestedSkill: Skill) {
        emit('skillSelected', suggestedSkill);
    }
</script>
<style lang="scss" scoped>
    .experience-skills-suggestions {
        margin-top: var(--joy-core-spacing-3);

        &__tags {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: var(--joy-core-spacing-3);
        }

        &__item {
            cursor: pointer;
        }

        &__plus {
            cursor: pointer;
            font-size: var(--joy-font-size-primary-400);
        }
    }
</style>
